import React, { useMemo } from 'react';
import {
    Button,
    MessageBar,
    MessageBarActions,
    MessageBarBody,
    MessageBarIntent,
    MessageBarTitle,
    makeStyles,
    shorthands,
} from '@fluentui/react-components';
import { DismissRegular, MegaphoneFilled } from '@fluentui/react-icons';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { setShowNotificationBannerDialog } from '../../../redux/features/notificationBanner/notificationBannerSlice';
import { NotificationBannerConfig, NotificationBannerFeature } from '../../../domain/chat/models/NotificationBanner';
import { customColors } from '../../../styles';

const useStyles = makeStyles({
    messageBar: {
        color: customColors.white,
        ...shorthands.borderColor(customColors.protivitiOrange),
        background: customColors.protivitiOrangeGradient,
        width: '100%',
        textAlign: 'center',
        minHeight: '32px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        paddingTop: '0px',
        '@media (max-width: 1200px)': {
            paddingTop: '10px',
            paddingBottom: '10px',
        },
    },
    navLink: {
        textDecoration: 'underline',
        color: 'rgb(13,110,253)',
        ':hover': {
            cursor: 'pointer',
            color: 'rgb(10,88,202)',
        },
    },
});

const validateCTA = (cta: unknown): boolean => {
    if (!cta || typeof cta !== 'object') {
        return false;
    }

    const candidate = cta as Record<string, unknown>;

    return (
        ['Internal', 'External', 'None'].includes(candidate.type as string) &&
        typeof candidate.ref === 'string' &&
        typeof candidate.agentId === 'string' &&
        typeof candidate.tab === 'string' &&
        typeof candidate.buttonText === 'string'
    );
};

const validateFeature = (feature: unknown): feature is NotificationBannerFeature => {
    if (!feature || typeof feature !== 'object') {
        return false;
    }

    const candidate = feature as Record<string, unknown>;

    return (
        typeof candidate.title === 'string' &&
        typeof candidate.message === 'string' &&
        (candidate.image === undefined || typeof candidate.image === 'string') &&
        (candidate.type === undefined || typeof candidate.type === 'string') &&
        validateCTA(candidate.CTA)
    );
};

const parseBannerConfig = (
    config: Partial<NotificationBannerConfig>,
): { config?: NotificationBannerConfig; features: NotificationBannerFeature[] } => {
    if (!config.bannerMessage || !config.category || !config.startDate || !config.endDate || !config.showDialog) {
        return { features: [] };
    }

    const features = Array.isArray(config.features) ? config.features.filter(validateFeature) : [];

    return { config: config as NotificationBannerConfig, features };
};

const parseBannerText = (
    text: string,
    navigate: ReturnType<typeof useNavigate>,
    className: string,
): React.ReactNode[] => {
    const regex = /\[([^\]]+)\]\(([^)]+)\)/g;
    const parts: React.ReactNode[] = [];
    let lastIndex = 0;
    let match: RegExpExecArray | null;

    while ((match = regex.exec(text)) !== null) {
        if (match.index > lastIndex) {
            parts.push(text.substring(lastIndex, match.index));
        }

        const label = match[1];
        const url = match[2];

        if (url.startsWith('navigate:')) {
            const path = url.replace('navigate:', '');
            parts.push(
                <span className={className} key={match.index} onClick={() => navigate(path)}>
                    {label}
                </span>,
            );
        } else {
            parts.push(
                <a href={url} key={match.index} target="_blank" rel="noopener noreferrer">
                    {label}
                </a>,
            );
        }

        lastIndex = regex.lastIndex;
    }

    if (lastIndex < text.length) {
        parts.push(text.substring(lastIndex));
    }

    return parts;
};

export interface NotificationBannerProps {
    onClose: () => void;
}

export const NotificationBanner: React.FC<NotificationBannerProps> = ({ onClose }) => {
    const classes = useStyles();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const bannerConfig = useAppSelector((state) => state.notificationBanner.bannerConfig);

    const { config, features } = useMemo(() => parseBannerConfig(bannerConfig), [bannerConfig]);

    if (!config) {
        return null;
    }

    const bannerText = parseBannerText(config.bannerMessage, navigate, classes.navLink);
    const showDialog = config.showDialog === 'true' && features.length > 0;

    return (
        <MessageBar
            className={classes.messageBar}
            shape="square"
            intent={config.category as MessageBarIntent}
            layout="multiline"
            icon={config.category === 'announcement' ? <MegaphoneFilled primaryFill={customColors.white} /> : undefined}
        >
            <MessageBarBody>
                <MessageBarTitle>
                    <span>{bannerText}</span>
                    {showDialog ? (
                        <span
                            className={classes.navLink}
                            style={{ marginLeft: '15px' }}
                            onClick={() => dispatch(setShowNotificationBannerDialog(true))}
                        >
                            Learn More
                        </span>
                    ) : null}
                </MessageBarTitle>
            </MessageBarBody>
            <MessageBarActions
                containerAction={
                    <Button
                        aria-label="dismiss"
                        appearance="transparent"
                        icon={<DismissRegular primaryFill={customColors.white} />}
                        onClick={onClose}
                    />
                }
            />
        </MessageBar>
    );
};

export default NotificationBanner;
